/*───────────────────────────────────────────────────────────────
 📄 File: hooks/useCdrFilterForm.ts
 🧩 Description:
    Form state, load, validation, save, and condition management
    for CDR Filter form. Extracted from CdrFilterFormView.tsx.
────────────────────────────────────────────────────────────────*/

import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useI18n } from '../../../hooks/useI18n';
import { useNotifications } from '../../../contexts/NotificationContext';
import { useSaveWithAction } from '../../../hooks/useSaveWithAction';
import {
    CdrFilter,
    CdrFilterData,
    SearchConditionItem,
    SearchMode,
    DurationRange,
    TalkTimeRange,
    CreateCdrFilterPayload,
    UpdateCdrFilterPayload,
} from '../types';
import { fetchCdrFilter, createCdrFilter, updateCdrFilter } from '../api';

export const DEFAULT_CONDITION: SearchConditionItem = {
    condition: 'AND',
    searchBy: 'caller_id',
    value: '',
    exclude: false,
    mode: 'begins_with',
};

export function useCdrFilterForm() {
    const { id } = useParams<{ id: string }>();
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const { t } = useI18n();
    const { showSuccess, showError } = useNotifications();

    const isEditMode = Boolean(id && id !== 'new');
    const duplicateId = searchParams.get('duplicate');

    const [description, setDescription] = useState('');
    const [enabled, setEnabled] = useState(true);
    const [duration, setDuration] = useState<DurationRange>({ from: null, to: null });
    const [talkTime, setTalkTime] = useState<TalkTimeRange>({ from: null, to: null });
    const [searchConditions, setSearchConditions] = useState<SearchConditionItem[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [errors, setErrors] = useState<Record<string, string>>({});

    // Refs to prevent stale closures and infinite loops
    const lastLoadedIdRef = useRef<string | null>(null);
    const isLoadingRef = useRef<boolean>(false);
    const showErrorRef = useRef(showError);
    const tRef = useRef(t);
    const navigateRef = useRef(navigate);

    useEffect(() => {
        showErrorRef.current = showError;
        tRef.current = t;
        navigateRef.current = navigate;
    }, [showError, t, navigate]);

    // ── Load filter data ─────────────────────────────────────
    useEffect(() => {
        const loadFilter = async () => {
            if (!isEditMode && !duplicateId) {
                lastLoadedIdRef.current = null;
                setSearchConditions([{ ...DEFAULT_CONDITION }]);
                setDescription('');
                setEnabled(true);
                setDuration({ from: null, to: null });
                setTalkTime({ from: null, to: null });
                setErrors({});
                return;
            }

            const loadId = duplicateId || id;
            if (!loadId) { lastLoadedIdRef.current = null; return; }
            if (lastLoadedIdRef.current === loadId || isLoadingRef.current) return;

            lastLoadedIdRef.current = loadId;
            isLoadingRef.current = true;
            setIsLoading(true);
            try {
                const response = await fetchCdrFilter(loadId);
                const filter = response.data;

                setDescription(duplicateId ? `${filter.description} (Copy)` : filter.description);
                setEnabled(filter.enabled);
                setDuration(filter.filterData.duration || { from: null, to: null });
                setTalkTime(filter.filterData.talkTime || { from: null, to: null });

                const loadedConditions = filter.filterData.searchConditions?.length > 0
                    ? filter.filterData.searchConditions.map((cond: SearchConditionItem) => {
                        if (cond.searchBy === 'hangup_cause' || cond.searchBy === 'call_type') {
                            return { ...cond, exclude: false, mode: 'exactly' as SearchMode };
                        }
                        return cond;
                    })
                    : [{ ...DEFAULT_CONDITION }];

                setSearchConditions(loadedConditions);
                setErrors({});
            } catch (error: unknown) {
                lastLoadedIdRef.current = null;
                showErrorRef.current((error as Error)?.message || tRef.current('cdrFilters.messages.loadError'));
            } finally {
                setIsLoading(false);
                isLoadingRef.current = false;
            }
        };

        void loadFilter();
    }, [id, duplicateId, isEditMode]);

    // Reset guard on unmount
    useEffect(() => { return () => { lastLoadedIdRef.current = null; }; }, []);

    // ── Condition handlers ───────────────────────────────────
    const handleAddCondition = useCallback(() => {
        setSearchConditions(prev => [...prev, { ...DEFAULT_CONDITION }]);
    }, []);

    const handleRemoveCondition = useCallback((index: number) => {
        setSearchConditions(prev => {
            const next = prev.filter((_, i) => i !== index);
            return next.length > 0 ? next : [{ ...DEFAULT_CONDITION }];
        });
    }, []);

    const handleConditionChange = useCallback(
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (index: number, field: keyof SearchConditionItem, value: any) => {
            setSearchConditions(prev =>
                prev.map((cond, i) => {
                    if (i !== index) return cond;
                    if (field === 'searchBy') {
                        const selectBased = ['hangup_cause', 'call_type'];
                        if (selectBased.includes(value)) {
                            return { ...cond, [field]: value, value: '', exclude: false, mode: 'exactly' };
                        }
                        if (selectBased.includes(cond.searchBy) || cond.searchBy !== value) {
                            return { ...cond, [field]: value, value: '' };
                        }
                    }
                    return { ...cond, [field]: value };
                })
            );
        },
        []
    );

    // ── Validation ───────────────────────────────────────────
    const validateForm = useCallback((): boolean => {
        const newErrors: Record<string, string> = {};
        if (!description.trim()) newErrors.description = t('cdrFilters.messages.validation.descriptionRequired');
        if (searchConditions.length === 0) newErrors.searchConditions = t('cdrFilters.messages.validation.atLeastOneCondition');
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    }, [description, searchConditions, t]);

    // ── Save ─────────────────────────────────────────────────
    const handleSave = useCallback(async (): Promise<CdrFilter | undefined> => {
        if (!validateForm()) return undefined;

        setIsSaving(true);
        try {
            const filterData: CdrFilterData = {
                duration,
                talkTime,
                searchConditions: searchConditions.filter(c => c.value.trim().length > 0),
            };

            let savedData: CdrFilter;
            if (isEditMode && id) {
                const payload: UpdateCdrFilterPayload = { description: description.trim(), filterData, enabled };
                const response = await updateCdrFilter(id, payload);
                savedData = response.data;
            } else {
                const payload: CreateCdrFilterPayload = { description: description.trim(), filterData, enabled };
                const response = await createCdrFilter(payload);
                savedData = response.data;
            }
            showSuccess(t('cdrFilters.messages.saveSuccess'));
            return savedData;
        } catch (error: unknown) {
            showError((error as Error)?.message || t('cdrFilters.messages.saveError'));
            return undefined;
        } finally {
            setIsSaving(false);
        }
    }, [validateForm, description, enabled, duration, talkTime, searchConditions, isEditMode, id, showSuccess, showError, t]);

    const handleSaveInternal = useCallback(async () => {
        const saved = await handleSave();
        if (!saved && isEditMode && id) return { id: Number(id), uuid: String(id) } as CdrFilter;
        return saved;
    }, [handleSave, isEditMode, id]);

    const handleSaveWithAction = useSaveWithAction({
        onSave: handleSaveInternal,
        listPath: '/reports/cdr/filters',
        newPath: '/reports/cdr/filters/new',
        mode: isEditMode ? 'edit' : 'create',
        getEditPath: (saved) => `/reports/cdr/filters/${saved.id || saved.uuid}`,
    });

    const handleCancel = useCallback(() => navigate('/reports/cdr/filters'), [navigate]);

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const hasChanges = useMemo(() => true, []);

    return {
        // State
        id, isEditMode, duplicateId, description, enabled, duration, talkTime,
        searchConditions, isLoading, isSaving, errors,
        // Setters
        setDescription, setEnabled, setDuration, setTalkTime,
        // Handlers
        handleAddCondition, handleRemoveCondition, handleConditionChange,
        handleSaveWithAction, handleCancel,
    };
}
